// Admin → Ops → Health. The one graded answer to "is CXHub working right now",
// rendered from the same computation the public /status probe and the Teams
// alerts read (server/src/services/platform-status.js).
//
// The sibling API Status tab lists what CXHub is CONNECTED to. This one asks
// whether work is actually FLOWING: /ready goes green on a process that is up
// and a Postgres that answers, and stays green through an expired eBay token, a
// poller that stopped ticking, or an AI backlog nobody is draining. Each of
// those is a check here, graded and grouped.
//
// Nothing on this page is customer content — counts, timestamps, flag states
// and short operator-facing error strings only.

import { useEffect, useState } from 'react'
import {
  Box, Typography, Stack, Chip, Paper, CircularProgress, Button, Alert, Collapse, Divider,
} from '@mui/material'
import { fetchPlatformStatus } from './api.js'

// The overall verdict, in words rather than a colour nobody can read aloud.
const OVERALL = {
  ok: { color: 'success', label: 'All systems working' },
  degraded: { color: 'warning', label: 'Degraded — something needs a look' },
  down: { color: 'error', label: 'Down — work is not flowing' },
}

// One check's grade → how it should read at a glance.
//
// `off` is deliberately quiet, the same posture ApiStatusView's `paused`
// mailbox state takes: a switched-off account or flag is SUPPOSED to be
// silent, and colouring that as a fault is how a health page becomes
// something people scroll past — at which point the one card that matters
// goes unread too.
const STATES = {
  ok: { color: 'success', label: 'ok', variant: 'filled' },
  warn: { color: 'warning', label: 'warning', variant: 'filled' },
  crit: { color: 'error', label: 'critical', variant: 'filled' },
  off: { color: 'default', label: 'off', variant: 'outlined' },
}

// Groups in the order an operator reads them: the platform itself, then what
// comes in, then what the AI does with it, then the credentials and automation
// that keep both alive. Anything the server adds later lands at the end rather
// than disappearing.
const GROUP_ORDER = ['Platform', 'Ingestion', 'AI', 'Credentials', 'Automation']

// Worst first inside a group — the whole point of the page is that the one bad
// card is the first thing you see.
const STATE_RANK = { crit: 0, warn: 1, ok: 2, off: 3 }

function formatMs(ms) {
  if (ms == null) return '—'
  if (ms < 1000) return `${ms}ms`
  if (ms < 60000) return `${Math.round(ms / 1000)}s`
  if (ms < 3600000) return `${Math.round(ms / 60000)} minutes`
  return `${Math.round(ms / 3600000)} hours`
}

function formatMinutes(n) {
  if (n == null) return '—'
  if (n >= 60 && n % 60 === 0) return `${n / 60} hour${n === 60 ? '' : 's'}`
  return `${n} minutes`
}

// Uptime as something a human says out loud ("3d 4h"), not 273600 seconds.
function formatUptime(sec) {
  if (sec == null) return '—'
  const d = Math.floor(sec / 86400)
  const h = Math.floor((sec % 86400) / 3600)
  const m = Math.floor((sec % 3600) / 60)
  if (d > 0) return `${d}d ${h}h`
  if (h > 0) return `${h}h ${m}m`
  if (m > 0) return `${m}m`
  return `${sec}s`
}

function when(ts) {
  return ts ? new Date(ts).toLocaleString() : null
}

// The thresholds the grades are made of, in human units. Printed rather than
// hidden in the service file so "why is this a warning" never needs a
// developer.
const THRESHOLD_ROWS = [
  { key: 'dbSlowMs', label: 'Postgres counts as slow above', format: formatMs },
  { key: 'pollerWarnMs', label: 'An email poller tick is late after', format: formatMs },
  { key: 'pollerCritMs', label: 'No email poller tick means the loop has stopped after', format: formatMs },
  // eBay is a full rescan (4 to 7 minutes a tick), so it gets longer limits.
  { key: 'ebayPollerWarnMs', label: 'An eBay poller tick is late after', format: formatMs },
  { key: 'ebayPollerCritMs', label: 'No eBay poller tick means the loop has stopped after', format: formatMs },
  { key: 'accountFailingCritMs', label: 'An account failing is an outage after', format: formatMs },
  { key: 'activityStaleMs', label: 'A live account with no messages goes stale after', format: formatMs },
  { key: 'backlogWarnMinutes', label: 'A conversation waiting for a draft is a warning after', format: formatMinutes },
  { key: 'backlogCritMinutes', label: 'A conversation waiting for a draft counts as badly late after', format: formatMinutes },
  { key: 'backlogCritCount', label: 'That many badly-late drafts make it critical', format: (n) => String(n) },
]

function CheckCard({ c }) {
  const s = STATES[c.state] ?? { color: 'default', label: c.state, variant: 'outlined' }
  const quiet = c.state === 'off'
  // Whichever of these the server sent — the graders attach different extras
  // per check, and an absent timestamp is simply not printed.
  const times = [
    c.lastSuccessAt && `last success ${when(c.lastSuccessAt)}`,
    c.lastRunAt && `last run ${when(c.lastRunAt)}`,
    c.lastActivityAt && `last message ${when(c.lastActivityAt)}`,
    c.lastSuggestionAt && `last draft ${when(c.lastSuggestionAt)}`,
    c.oldestAt && `oldest waiting ${when(c.oldestAt)}`,
    c.expiresOn && `expires ${when(c.expiresOn)}`,
  ].filter(Boolean)

  return (
    <Paper variant="outlined" sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: quiet ? 'text.secondary' : 'text.primary' }}>
          {c.name}
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Chip size="small" color={s.color} variant={s.variant} label={s.label} />
      </Box>
      {c.detail && (
        <Typography variant="body2" color={quiet ? 'text.disabled' : 'text.secondary'}>{c.detail}</Typography>
      )}
      {times.length > 0 && (
        <Typography variant="caption" color="text.disabled">{times.join(' · ')}</Typography>
      )}
      {c.lastError && (
        <Typography variant="caption" sx={{ fontFamily: 'monospace', color: quiet ? 'text.disabled' : 'error.main', wordBreak: 'break-word' }}>
          {c.lastError}
        </Typography>
      )}
    </Paper>
  )
}

function Thresholds({ thresholds }) {
  const [open, setOpen] = useState(false)
  if (!thresholds) return null
  return (
    <Box sx={{ mt: 2 }}>
      <Button size="small" onClick={() => setOpen((v) => !v)}>
        {open ? 'Hide thresholds' : 'Thresholds'}
      </Button>
      <Collapse in={open}>
        <Paper variant="outlined" sx={{ p: 2, mt: 1, maxWidth: '80ch' }}>
          <Stack spacing={0.5}>
            {THRESHOLD_ROWS.filter((r) => thresholds[r.key] != null).map((r) => (
              <Box key={r.key} sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>{r.label}</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{r.format(thresholds[r.key])}</Typography>
              </Box>
            ))}
          </Stack>
        </Paper>
      </Collapse>
    </Box>
  )
}

export default function HealthView() {
  const [status, setStatus] = useState(null)
  const [failed, setFailed] = useState(false)
  const [loading, setLoading] = useState(true)

  const load = ({ fresh = false } = {}) => {
    setLoading(true)
    return fetchPlatformStatus({ fresh }).then((r) => {
      // Keep the last good payload on screen when a read fails. Blanking the
      // page would read as "nothing to report" — precisely the wrong thing to
      // say at the moment we could not check.
      if (r.status) setStatus(r.status)
      setFailed(r.failed)
      setLoading(false)
    })
  }

  // First load and the 60s tick both take the server's cached copy (it
  // recomputes every 30s anyway); only the Refresh button pays for a fresh one.
  useEffect(() => {
    load()
    const timer = setInterval(() => load(), 60000)
    return () => clearInterval(timer)
  }, [])

  const overall = OVERALL[status?.status] ?? { color: 'default', label: 'unknown' }

  // Group, then order the groups, then order the cards inside each.
  const byGroup = (status?.checks ?? []).reduce((acc, c) => {
    (acc[c.group ?? 'Other'] ??= []).push(c)
    return acc
  }, {})
  const groups = Object.keys(byGroup).sort((a, b) => {
    const ai = GROUP_ORDER.indexOf(a)
    const bi = GROUP_ORDER.indexOf(b)
    return (ai === -1 ? GROUP_ORDER.length : ai) - (bi === -1 ? GROUP_ORDER.length : bi)
  })

  const p = status?.process

  return (
    <Box sx={{ flex: 1, p: 3, overflowY: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1, flexWrap: 'wrap' }}>
        <Typography variant="h6">Health</Typography>
        {status && <Chip color={overall.color} label={overall.label} sx={{ fontWeight: 600 }} />}
        {status?.checkedAt && (
          <Typography variant="caption" color="text.secondary">
            checked {new Date(status.checkedAt).toLocaleString()}
          </Typography>
        )}
        <Box sx={{ flexGrow: 1 }} />
        <Button size="small" onClick={() => load({ fresh: true })} disabled={loading}>Refresh</Button>
      </Box>

      {p && (
        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mb: 2 }}>
          {p.deployEnv ?? '—'} · revision {p.revision ?? '—'} · replica {p.replica ?? '—'} · up {formatUptime(p.uptimeSec)} · node {p.node ?? '—'}
        </Typography>
      )}

      {failed && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Could not read platform status. This says nothing about whether the platform is working.
        </Alert>
      )}

      {status && status.dbReachable === false && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Postgres is unreachable, so the rest of the checks could not run — they are missing, not passing.
        </Alert>
      )}

      {!status && loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : !status ? (
        <Typography color="text.secondary">No status reported.</Typography>
      ) : (
        <Stack spacing={3}>
          {groups.map((group) => (
            <Box key={group}>
              <Typography variant="overline" color="primary">{group}</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 2, mt: 0.5 }}>
                {byGroup[group]
                  .slice()
                  .sort((a, b) => (STATE_RANK[a.state] ?? 9) - (STATE_RANK[b.state] ?? 9))
                  .map((c) => <CheckCard key={c.key} c={c} />)}
              </Box>
            </Box>
          ))}
        </Stack>
      )}

      <Divider sx={{ my: 3 }} />

      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: '80ch' }}>
        Every check is graded <b>ok</b>, <b>warning</b>, <b>critical</b> or <b>off</b>. One critical check
        makes the whole platform <b>Down</b>; one warning makes it <b>Degraded</b>; otherwise it reads
        <b> All systems working</b>. <b>Off</b> means a feature flag or a Run-Time Flags switch turned
        that piece off on purpose — it is reported so you can see it, and it never counts against the
        overall grade. This is the same verdict the public /status probe returns to Azure&apos;s
        availability test and the same one the status heartbeat sends to the Teams alerts, so what you
        read here is what pages someone. The page refreshes itself every minute.
      </Typography>

      <Thresholds thresholds={status?.thresholds} />
    </Box>
  )
}
